/**
 * investMode — persistence + resolution for the INVEST nav mode.
 * @version v0.1 (2026-05-21)
 *
 * The INVEST section serves two audiences: founders raising money and
 * investors deploying it. The user picks one in the sidebar; we store it
 * on user_preferences.invest_mode and read it back on load. The
 * pipeline-crm route also reads it as the default for its ?mode param.
 *
 * Values: 'founder' | 'investor' | null (unset → show the selector).
 *
 * @version v0.2 (2026-05-21) — adds the useInvestMode() hook the founder-mode
 *   surfaces (My Thesis / My Cap Table / Anti-Investors) gate on.
 * @version v0.3 (2026-05-23, Dispatch D) — adds the "both" mode (user invests
 *   AND raises) plus hasInvestorAccess()/hasFounderAccess() helpers the nav and
 *   founder-mode pages use so a "both" user sees both surfaces.
 */
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";

/**
 * v0.4 (2026-08-13) — "off" is a mode, and unset no longer means "investor".
 *
 * resolveInvestMode() used to return "investor" for anybody who had never
 * chosen. So a solo founder who never touched this got My Thesis, deal-vetting
 * and investor matching applied to him by default, and there was no way to say
 * no — the selector offered Founder or Investor and nothing else.
 *
 * "off" hides the whole INVEST section and every surface inside it. Unset now
 * resolves to "off" as well: not choosing is not a preference for the investor
 * framing, and a section that appears because nobody said no is a section
 * nobody asked for.
 */
export type InvestMode = "founder" | "investor" | "both" | "off";

export function isInvestMode(v: unknown): v is InvestMode {
  return v === "founder" || v === "investor" || v === "both" || v === "off";
}

/** True when the INVEST section and everything it implies is switched off. */
export function investIsOff(mode: InvestMode | null | undefined): boolean {
  return mode === "off" || mode == null;
}

/** True when the mode grants investor-side surfaces (investor or both). */
export function hasInvestorAccess(mode: InvestMode | null | undefined): boolean {
  return mode === "investor" || mode === "both";
}

/** True when the mode grants founder-side surfaces (founder or both). */
export function hasFounderAccess(mode: InvestMode | null | undefined): boolean {
  return mode === "founder" || mode === "both";
}

/** Read the saved invest_mode for a user. Returns null when unset. */
export async function getInvestMode(userId: string): Promise<InvestMode | null> {
  if (!userId) return null;
  try {
    const { data } = await (supabase.from("user_preferences") as any)
      .select("invest_mode")
      .eq("user_id", userId)
      .maybeSingle();
    const v = (data as any)?.invest_mode;
    return isInvestMode(v) ? v : null;
  } catch {
    return null;
  }
}

/** Persist the chosen invest_mode. Fire-and-forget friendly. */
export async function setInvestMode(userId: string, mode: InvestMode): Promise<void> {
  if (!userId) return;
  await (supabase.from("user_preferences") as any).upsert(
    { user_id: userId, invest_mode: mode, updated_at: new Date().toISOString() },
    { onConflict: "user_id" },
  );
}

/**
 * Resolve the effective mode for a surface that accepts a ?mode override
 * (e.g. pipeline-crm). Explicit URL param wins; otherwise the saved
 * preference; otherwise 'off'.
 */
export function resolveInvestMode(urlParam: string | null, saved: InvestMode | null): InvestMode {
  if (isInvestMode(urlParam)) return urlParam;
  if (saved) return saved;
  // NOT "investor". Nobody having chosen is not consent to the investor
  // framing; it is the absence of a choice, and the honest reading of that is
  // "do not show me this".
  return "off";
}

/**
 * React hook the founder-mode pages gate on. Resolves the effective mode the
 * same way pipeline-crm does — explicit ?mode= URL param wins, else the saved
 * user_preferences.invest_mode, else 'off'. Returns `loading` so a page
 * can defer its redirect until the saved preference has actually loaded (so a
 * founder isn't bounced to the investor surface on the first paint).
 */
export function useInvestMode(): { mode: InvestMode; loading: boolean; userId: string | null } {
  const [params] = useSearchParams();
  const [userId, setUserId] = useState<string | null>(null);
  const [saved, setSaved] = useState<InvestMode | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data } = await supabase.auth.getUser();
      const uid = data.user?.id || null;
      if (cancelled) return;
      setUserId(uid);
      if (uid) {
        try {
          const m = await getInvestMode(uid);
          if (!cancelled) setSaved(m);
        } catch { /* keep null */ }
      }
      if (!cancelled) setLoading(false);
    })();
    return () => { cancelled = true; };
  }, []);

  return { mode: resolveInvestMode(params.get("mode"), saved), loading, userId };
}
